import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  ÁLLOMÁNY-ÖSSZEVONÁS – ugyanaz a dolgozó több felhasználónévvel.
 *
 *  A gondviselői összevonás (Családszerkesztő) párja. A duplikátum tipikusan azért
 *  keletkezik, mert a második fiók MÁSIK feladatellátásnál jött létre – ezért a párt
 *  a rendszer minden intézményben keresi, de csak akkor mutatja, ha van benne saját
 *  dolgozó.
 *
 *  Az összevonás visszafordíthatatlan, ezért két lépés: előbb PRÓBA (mi változna),
 *  és csak külön kattintásra az írás.
 */
@Component({
  selector: 'app-allomany-osszevonas',
  templateUrl: './allomany-osszevonas.component.html',
  styleUrls: ['./allomany-osszevonas.component.scss'],
})
export class AllomanyOsszevonasComponent implements OnInit {
  intezmeny = '';
  toltodik = false;
  fut = false;
  duplikatumok: any[] = null;

  /** A megnyitott munkalap: melyik fiók marad, melyek olvadnak be. */
  munkalap: any = null;
  terv: any = null;

  tomegesTerv: any = null;

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    this.intezmeny = (await this.data.getFromStorage('sajatIntezmeny'))?.intezmenyId || '';
    await this.betolt();
  }

  async betolt() {
    if (!this.intezmeny) return;
    this.toltodik = true;
    this.munkalap = null; this.terv = null; this.tomegesTerv = null;
    try {
      const r: any = await firstValueFrom(this.http.get(
        `/api/allomany-osszevonas/duplikatumok/${this.intezmeny}`));
      this.duplikatumok = r?.duplikatumok || [];
    } catch (e: any) {
      this.duplikatumok = null;
      this.data.sendToast(e?.error?.error || 'A duplikátumok lekérése nem sikerült.', 'error');
    } finally {
      this.toltodik = false;
    }
  }

  /** Egy jelölt megnyitása: alapból a javasolt fiók marad, a többi beolvad. */
  megnyit(j: any) {
    this.munkalap = {
      nev: j.nev, bizonytalan: j.bizonytalan, bizonytalan_ok: j.bizonytalan_ok,
      fiokok: j.fiokok,
      megmarad: j.javasolt,
      beolvad: j.fiokok.map((f: any) => f.adid).filter((a: string) => a !== j.javasolt),
    };
    this.terv = null;
  }

  megmaradValt(adid: string) {
    this.munkalap.megmarad = adid;
    // A megmaradó nem olvadhat be önmagába; a többi alapból beolvad.
    this.munkalap.beolvad = this.munkalap.fiokok
      .map((f: any) => f.adid).filter((a: string) => a !== adid);
    this.terv = null;
  }

  beolvadValt(adid: string) {
    const i = this.munkalap.beolvad.indexOf(adid);
    if (i >= 0) this.munkalap.beolvad.splice(i, 1);
    else this.munkalap.beolvad.push(adid);
    this.terv = null;
  }

  hasznalatSorok(f: any): string[] {
    return Object.keys(f.hasznalat || {}).map((k) => `${k}: ${f.hasznalat[k]}`);
  }

  async proba() { await this.kuld(true); }

  async vegleges() {
    const mit = this.munkalap.beolvad.join(', ');
    if (!confirm(`Véglegesen összevonod? A(z) ${mit} fiók(ok) adatai átkerülnek a(z) `
      + `${this.munkalap.megmarad} fiókhoz, a beolvadó fiók pedig TÖRLŐDIK. `
      + 'Ez nem vonható vissza.')) return;
    await this.kuld(false);
  }

  private async kuld(dryRun: boolean) {
    if (!this.munkalap?.beolvad?.length) {
      this.data.sendToast('Jelölj ki legalább egy beolvadó fiókot.', 'warning');
      return;
    }
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post('/api/allomany-osszevonas/osszevonas', {
        megmarad: this.munkalap.megmarad,
        beolvad: this.munkalap.beolvad,
        dry_run: dryRun,
      }));
      this.terv = r;
      if (!dryRun) {
        this.data.sendToast('Összevonva.', 'success');
        await this.betolt();
      }
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A művelet nem sikerült.', 'error');
    } finally {
      this.fut = false;
    }
  }

  async tomegesProba() {
    this.fut = true;
    try {
      this.tomegesTerv = await firstValueFrom(this.http.post(
        `/api/allomany-osszevonas/osszevonas-tomeges/${this.intezmeny}`, { dry_run: true }));
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A tömeges próba nem sikerült.', 'error');
    } finally { this.fut = false; }
  }

  async tomegesVegleges() {
    const db = this.tomegesTerv?.osszesites?.osszevonhato || 0;
    if (!db) return;
    if (!confirm(`${db} egyértelmű duplikátum összevonása. A beolvadó fiókok TÖRLŐDNEK, `
      + 'és ez nem vonható vissza. A bizonytalanokhoz nem nyúlunk. Folytatod?')) return;
    this.fut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(
        `/api/allomany-osszevonas/osszevonas-tomeges/${this.intezmeny}`, { dry_run: false }));
      this.data.sendToast(`${r?.eredmenyek?.filter((e: any) => !e.hiba).length || 0} összevonás kész.`, 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A tömeges összevonás nem sikerült.', 'error');
    } finally { this.fut = false; }
  }
}
